import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import type { IconName } from '../../../../components/AppIcon';
import { getAssignmentHistory } from '@/api/TournamentManagement/refereeApi';
import { AssignmentAction } from '@/models/tournament';
import type { AssignmentHistoryView } from '@/models/tournament';
import { parseApiUtc } from '@/utils/apiDate';
import { appDateTime } from '@/utils/appDateFormat';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { parseReconciledReason, reconcileTriggerKey } from '@/utils/tournamentPostCompletion';

/**
 * CO-921 (design section 9): HR-only, read-only assignment history for one match. No web view showed
 * it before, so HR could not see who moved a match's work points. Progressive disclosure: nothing is
 * fetched until HR opens it, and it reloads every time it is opened so a replacement made in the
 * Assignment Center shows up without closing the match.
 *
 * Reasons are NOT rendered raw: the client and server write them in English. A WorkPointsReconciled
 * row is parsed into its points change and a translated trigger; other rows show the action and names.
 */
const ACTION_KEYS: Record<number, string> = {
  [AssignmentAction.Assigned]: 'wpFollow.action.assigned',
  [AssignmentAction.Reassigned]: 'wpFollow.action.reassigned',
  [AssignmentAction.Removed]: 'wpFollow.action.removed',
  [AssignmentAction.AcceptedByReferee]: 'wpFollow.action.accepted',
  [AssignmentAction.DeclinedByReferee]: 'wpFollow.action.declined',
  [AssignmentAction.NotificationFailed]: 'wpFollow.action.notificationFailed',
  [AssignmentAction.ConfirmedByHr]: 'wpFollow.action.confirmedByHr',
  [AssignmentAction.Lapsed]: 'wpFollow.action.lapsed',
  [AssignmentAction.WorkPointsReconciled]: 'wpFollow.action.pointsChanged',
};

const ACTION_ICONS: Record<number, IconName> = {
  [AssignmentAction.Assigned]: 'UserPlus',
  [AssignmentAction.Reassigned]: 'Repeat',
  [AssignmentAction.Removed]: 'UserMinus',
  [AssignmentAction.AcceptedByReferee]: 'CheckCircle',
  [AssignmentAction.DeclinedByReferee]: 'XCircle',
  [AssignmentAction.NotificationFailed]: 'BellOff',
  [AssignmentAction.ConfirmedByHr]: 'ShieldCheck',
  [AssignmentAction.Lapsed]: 'Clock',
  [AssignmentAction.WorkPointsReconciled]: 'Coins',
};

interface AssignmentHistorySectionProps {
  matchId: number;
}

const AssignmentHistorySection: React.FC<AssignmentHistorySectionProps> = ({ matchId }) => {
  const { t } = useTranslation('tournament');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<AssignmentHistoryView[] | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAssignmentHistory(matchId);
      if (result.errors.length > 0) {
        setError(result.errors[0]?.description || t('wpFollow.history.loadFailed'));
        return;
      }
      setRows(result.payload ?? []);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, t, t('wpFollow.history.loadFailed'), ' '));
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) void load();
  };

  const formatWhen = (iso: string) => {
    const d = parseApiUtc(iso) ?? new Date(iso);
    return appDateTime(d);
  };

  const panelId = `assignment-history-${matchId}`;

  return (
    <section className="border-t border-border pt-4" data-testid="match-assignment-history">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center justify-between gap-2 rounded-md py-1 text-start text-sm font-semibold text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
        data-testid="match-assignment-history-toggle"
      >
        <span className="flex items-center gap-2">
          <Icon name="History" size={16} className="text-muted-foreground" />
          {t('wpFollow.history.title')}
        </span>
        <Icon name="ChevronDown" size={16} className={`text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div id={panelId} className="mt-3" aria-busy={loading}>
          {loading && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon name="Loader2" size={14} className="animate-spin" />
              {t('wpFollow.history.loading')}
            </p>
          )}
          {!loading && error && (
            <p role="alert" className="text-sm text-destructive">{error}</p>
          )}
          {!loading && !error && rows && rows.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('wpFollow.history.empty')}</p>
          )}
          {!loading && !error && rows && rows.length > 0 && (
            <ol className="space-y-2" data-testid="match-assignment-history-list">
              {rows.map((row) => {
                const action = Number(row.action);
                const reconciled = action === AssignmentAction.WorkPointsReconciled
                  ? parseReconciledReason(row.reason)
                  : null;
                const triggerKey = reconciled ? reconcileTriggerKey(reconciled.trigger) : null;
                const lost = reconciled != null && reconciled.to < reconciled.from;
                return (
                  <li
                    key={row.id}
                    className="flex items-start gap-3 rounded-md border border-border px-3 py-2"
                    data-testid={`match-assignment-history-row-${row.id}`}
                    data-action={action}
                  >
                    <Icon
                      name={ACTION_ICONS[action] ?? 'Circle'}
                      size={14}
                      className={`mt-0.5 shrink-0 ${reconciled ? (lost ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400') : 'text-muted-foreground'}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground">
                        <span className="font-medium">{t(ACTION_KEYS[action] ?? 'wpFollow.action.other')}</span>
                        <span className="text-muted-foreground"> · </span>
                        {action === AssignmentAction.Reassigned && row.previousRefereeName
                          ? t('wpFollow.history.replacedBy', { previous: row.previousRefereeName, current: row.refereeName })
                          : row.refereeName}
                      </p>
                      {reconciled && (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          <span className={`font-semibold ${lost ? 'text-red-700 dark:text-red-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
                            {t('wpFollow.history.points', { from: reconciled.from, to: reconciled.to })}
                          </span>
                          {triggerKey && <span>{' · '}{t(triggerKey)}</span>}
                        </p>
                      )}
                    </div>
                    <time dateTime={row.createdOn} className="shrink-0 text-xs text-muted-foreground tabular-nums">
                      {formatWhen(row.createdOn)}
                    </time>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </section>
  );
};

export default AssignmentHistorySection;
